import { useCallback, useEffect, useId, useMemo, useState } from "react";
import type { SecurityActivityResponse } from "@starsach/shared-types";
import { useApi } from "../../api/hooks";
import { useAuth } from "../../auth/AuthProvider";
import { cognitoApi, credentialToJson, passkeysPossible, toWebAuthnCreationOptions, type PasskeyRecord } from "../../auth/cognito-api";
import { useStepUp } from "../../auth/StepUpProvider";
import { useInvalidatePasskeys } from "../../auth/usePasskeys";
import { Card, DataTable, EmptyState, ErrorState, LoadingState, RelativeTime, StatusPill, btnPrimary, btnSecondary, inputClass } from "../../components/ui";
import { useFormat } from "../../lib/useFormat";

/**
 * Requirements 13.3. Everything here is the browser talking to Cognito with
 * the user's own access token - no route on our API can change how someone
 * signs in. The one exception is the activity view, which the API assembles
 * from Cognito's history and the user's own audit rows.
 */
export function SecuritySection() {
  const { session, config, me } = useAuth();
  const api = useMemo(() => (config ? cognitoApi(config) : undefined), [config]);
  const token = session?.accessToken;
  if (!api || !token) return null;
  if (me?.viewAs) {
    return (
      <Card title="Security">
        <p className="text-sm text-neutral-600 dark:text-neutral-400">Sign-in settings are your own and are managed from your own tab, not from inside a customer's portal.</p>
      </Card>
    );
  }
  if (me?.identityProvider) {
    // ADR-0014: password, MFA, passkeys and remembered devices are the provider's; Cognito holds none of them for this user.
    return (
      <div id="security" className="scroll-mt-6 space-y-6">
        <Card title="Sign-in">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            You sign in through <span className="font-medium text-ink dark:text-white">{me.identityProvider}</span>, your organisation's identity provider. Your password, multi-factor authentication, passkeys and trusted devices are managed there, not here.
          </p>
        </Card>
        <SecurityActivity />
      </div>
    );
  }
  return (
    <div id="security" className="scroll-mt-6 space-y-6">
      <Passkeys api={api} token={token} />
      <Authenticator api={api} token={token} />
      <Password api={api} token={token} />
      <SecurityActivity />
    </div>
  );
}

type Api = ReturnType<typeof cognitoApi>;

/** WebAuthn: a credential answers only on its relying party or a subdomain of it. */
export function usableHere(c: Pick<PasskeyRecord, "RelyingPartyId">, hostname = typeof window === "undefined" ? "" : window.location.hostname): boolean {
  const rp = c.RelyingPartyId?.toLowerCase();
  if (!rp) return true;
  const host = hostname.toLowerCase();
  return host === rp || host.endsWith(`.${rp}`);
}

function useCognitoList<T>(load: () => Promise<T>) {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<unknown>();
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      setData(await load());
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [load]);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { data, error, loading, reload };
}

/** 13.3 [20]. The public key and credential id live in Cognito; the private key never leaves the authenticator. */
function Passkeys({ api, token }: { api: Api; token: string }) {
  const { ensureFresh } = useStepUp();
  const list = useCognitoList(useCallback(() => api.listPasskeys(token).then((r) => r.Credentials ?? []), [api, token]));
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  const id = useId();
  const supported = passkeysPossible();
  const invalidatePasskeys = useInvalidatePasskeys();

  const enrol = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await ensureFresh();
      const start = await api.startPasskey(token);
      const created = await navigator.credentials.create(toWebAuthnCreationOptions(start.CredentialCreationOptions));
      await api.completePasskey(token, credentialToJson(created));
      setLabel("");
      await list.reload();
      void invalidatePasskeys();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const remove = async (c: PasskeyRecord) => {
    setBusy(true);
    setError(undefined);
    try {
      await ensureFresh();
      await api.deletePasskey(token, c.CredentialId);
      await list.reload();
      void invalidatePasskeys();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Passkeys">
      <p className="text-sm text-neutral-600 dark:text-neutral-400">
        A passkey signs you in with your fingerprint, face or security key instead of a password and code, and answers the "confirm it's you" step with a touch. Enrol one per device you sign in from.
      </p>
      {!supported && <p className="mt-2 text-sm text-amber-800 dark:text-amber-300">This browser does not support passkeys.</p>}
      {list.loading && <LoadingState label="Loading passkeys" />}
      {list.error !== undefined && <ErrorState error={list.error} onRetry={() => void list.reload()} />}
      {list.data && (
        <div className="mt-3">
          <DataTable
            caption="Your passkeys"
            rows={list.data}
            rowKey={(c) => c.CredentialId}
            filter={false}
            empty={<EmptyState title="No passkeys yet" hint="Enrol one below; you will keep your authenticator app as well." />}
            columns={[
              { key: "name", header: "Name", render: (c) => c.FriendlyCredentialName ?? "Passkey" },
              { key: "kind", header: "Kind", render: (c) => (c.AuthenticatorAttachment === "platform" ? "This device" : c.AuthenticatorAttachment === "cross-platform" ? "Security key" : "—") },
              // WebAuthn's own rule: a credential answers only on its relying party or a subdomain of it. Cognito keeps the
              // record when the relying-party id changes (it happened on 2026-09-14), so the row must say it is dead.
              { key: "usable", header: "Usable here", render: (c) => (usableHere(c) ? <StatusPill status="yes" tone="ok" /> : <span title={`Enrolled for ${c.RelyingPartyId ?? "another site"}; remove it and enrol again.`}><StatusPill status="no" tone="warning" /></span>) },
              { key: "since", header: "Enrolled", render: (c) => (c.CreatedAt ? <RelativeTime iso={new Date(c.CreatedAt * 1000).toISOString()} /> : "—") },
              { key: "act", header: "", render: (c) => <button type="button" className={`${btnSecondary} min-h-8 px-3 text-xs`} disabled={busy} onClick={() => void remove(c)}>Remove</button> },
            ]}
          />
        </div>
      )}
      <form
        className="mt-4 flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          void enrol();
        }}
      >
        <label htmlFor={id} className="text-sm">
          <span className="block font-medium">Name for this passkey</span>
          <input id={id} className={`${inputClass} w-64`} value={label} placeholder="MacBook Touch ID" onChange={(e) => setLabel(e.target.value)} />
        </label>
        <button type="submit" className={btnPrimary} disabled={!supported || busy}>
          {busy ? "Working…" : "Enrol a passkey"}
        </button>
      </form>
      {error !== undefined && <ErrorState error={error} />}
    </Card>
  );
}

/** 13.3 [15]. A new authenticator replaces the old one once its first code is verified; the account is never without one. */
function Authenticator({ api, token }: { api: Api; token: string }) {
  const { session } = useAuth();
  const { ensureFresh } = useStepUp();
  const [secret, setSecret] = useState<string>();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<unknown>();
  const id = useId();
  const otpauth = secret ? `otpauth://totp/Starsach:${encodeURIComponent(session?.email ?? "you")}?secret=${secret}&issuer=Starsach` : undefined;

  const begin = async () => {
    setBusy(true);
    setError(undefined);
    setDone(false);
    try {
      await ensureFresh();
      const r = await api.associateSoftwareToken(token);
      if (!r.SecretCode) throw new Error("Cognito returned no secret");
      setSecret(r.SecretCode);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const verify = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const r = await api.verifySoftwareToken(token, code, "Authenticator app");
      if (r.Status !== "SUCCESS") throw new Error("The code did not match; try the next one.");
      // Managed login's own MFA setup leaves the per-user preference unset (the pool requires MFA anyway); enrolling here records it.
      await api.setTotpPreference(token);
      setSecret(undefined);
      setCode("");
      setDone(true);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Authenticator app">
      <p className="text-sm text-neutral-600 dark:text-neutral-400">
        Required for every password sign-in, so yours is set up. Cognito keeps one authenticator per account and does not record when it was set up. Moving to a new phone means enrolling it here; the old one stops working once the new one's first code is verified.
      </p>
      {!secret ? (
        <button type="button" className={`${btnSecondary} mt-3`} disabled={busy} onClick={() => void begin()}>
          Enrol a new authenticator
        </button>
      ) : (
        <form
          className="mt-3 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void verify();
          }}
        >
          <p className="text-sm">Add this to your authenticator app, then enter the code it shows.</p>
          <p className="break-all rounded bg-neutral-100 p-2 font-mono text-xs dark:bg-neutral-800" aria-label="Authenticator secret">{secret}</p>
          <a href={otpauth} className="text-sm text-accent-strong underline dark:text-accent">Open in an authenticator app on this device</a>
          <label htmlFor={id} className="block text-sm">
            <span className="block font-medium">Code from the app</span>
            <input id={id} inputMode="numeric" autoComplete="one-time-code" className={`${inputClass} w-40`} value={code} pattern="[0-9]{6}" required onChange={(e) => setCode(e.target.value)} />
          </label>
          <div className="flex gap-2">
            <button type="submit" className={btnPrimary} disabled={busy}>Verify and switch</button>
            <button type="button" className={btnSecondary} onClick={() => setSecret(undefined)}>Cancel</button>
          </div>
        </form>
      )}
      {done && <p role="status" className="mt-2 text-sm text-green-700 dark:text-green-300">Your new authenticator is active.</p>}
      {error !== undefined && <ErrorState error={error} />}
    </Card>
  );
}

/** 13.3 [14] and [16]. Cognito enforces the policy and history; every other session is signed out afterwards. */
function Password({ api, token }: { api: Api; token: string }) {
  const { signOut } = useAuth();
  const { ensureFresh } = useStepUp();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  const ids = { current: useId(), next: useId(), again: useId() };
  const mismatch = again.length > 0 && next !== again;

  const change = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await ensureFresh();
      await api.changePassword(token, current, next);
      // Every other session is out; this one signs in again with the new password.
      await api.globalSignOut(token);
      await signOut();
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };
  const everywhere = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await ensureFresh();
      await api.globalSignOut(token);
      await signOut();
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };

  return (
    <Card title="Password and sessions">
      <form
        className="grid gap-3 md:grid-cols-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!mismatch) void change();
        }}
      >
        <label htmlFor={ids.current} className="text-sm">
          <span className="block font-medium">Current password</span>
          <input id={ids.current} type="password" autoComplete="current-password" className={inputClass} value={current} required onChange={(e) => setCurrent(e.target.value)} />
        </label>
        <label htmlFor={ids.next} className="text-sm">
          <span className="block font-medium">New password</span>
          <input id={ids.next} type="password" autoComplete="new-password" className={inputClass} value={next} required minLength={12} onChange={(e) => setNext(e.target.value)} />
          <span className="mt-1 block text-xs text-neutral-500">At least 12 characters, with upper and lower case, a number and a symbol.</span>
        </label>
        <label htmlFor={ids.again} className="text-sm">
          <span className="block font-medium">New password again</span>
          <input id={ids.again} type="password" autoComplete="new-password" className={inputClass} value={again} required aria-invalid={mismatch || undefined} aria-describedby={mismatch ? `${ids.again}-err` : undefined} onChange={(e) => setAgain(e.target.value)} />
          {mismatch && <span id={`${ids.again}-err`} role="alert" className="mt-1 block text-xs text-red-700 dark:text-red-300">The two do not match.</span>}
        </label>
        <div className="md:col-span-3 flex flex-wrap items-center gap-3">
          <button type="submit" className={btnPrimary} disabled={busy || mismatch || !current || !next}>
            {busy ? "Working…" : "Change password"}
          </button>
          <span className="text-xs text-neutral-500">Changing it signs every session out, including this one; you sign in again with the new password.</span>
        </div>
      </form>
      <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-neutral-200 pt-4 dark:border-neutral-800">
        <button type="button" className={btnSecondary} disabled={busy} onClick={() => void everywhere()}>
          Sign out everywhere
        </button>
        <span className="text-xs text-neutral-500">Cognito cannot list where you are signed in, so this signs out every session - this one too.</span>
      </div>
      {error !== undefined && <ErrorState error={error} />}
    </Card>
  );
}

/** 13.3 [17]. Sign-ins from Cognito's history, and the user's own changes from the audit log, in their time zone. */
function SecurityActivity() {
  const api = useApi();
  const fmt = useFormat();
  const [data, setData] = useState<SecurityActivityResponse>();
  const [error, setError] = useState<unknown>();
  const [reported, setReported] = useState<Set<string>>(new Set());
  const load = useCallback(() => api<SecurityActivityResponse>("/v1/me/security-activity").then(setData).catch(setError), [api]);
  useEffect(() => {
    void load();
  }, [load]);
  const report = async (eventId: string) => {
    await api("/v1/me/security-report", { method: "POST", body: JSON.stringify({ eventId }) });
    setReported((s) => new Set(s).add(eventId));
  };

  return (
    <Card title="Security activity">
      <p className="text-sm text-neutral-600 dark:text-neutral-400">The last 90 days. If a sign-in is not yours, report it - Gairm is alerted at once.</p>
      {!data && error === undefined && <LoadingState label="Loading your activity" />}
      {error !== undefined && <ErrorState error={error} onRetry={() => void load()} />}
      {data && (
        <>
          {!data.signInsAvailable && <p className="mt-2 text-sm text-neutral-500">Sign-in history is not available on this deployment.</p>}
          <div className="mt-3">
            <DataTable
              caption="Sign-ins"
              rows={data.signIns}
              rowKey={(e) => e.id}
              empty={data.signInsAvailable ? <EmptyState title="No sign-ins recorded yet" /> : null}
              columns={[
                { key: "when", header: "When", render: (e) => <span title={e.at}>{fmt.when(e.at)}</span> },
                { key: "type", header: "Event", render: (e) => e.type },
                { key: "outcome", header: "Outcome", render: (e) => <StatusPill status={e.outcome.replace("_", " ")} tone={e.outcome === "pass" ? "ok" : e.outcome === "fail" ? "critical" : "neutral"} /> },
                { key: "where", header: "From", render: (e) => [e.ip, e.place].filter(Boolean).join(" · ") || "—" },
                { key: "device", header: "Device", render: (e) => e.device ?? "—" },
                {
                  key: "report",
                  header: "",
                  render: (e) =>
                    reported.has(e.id) ? (
                      <span className="text-xs text-green-700 dark:text-green-300">Reported</span>
                    ) : (
                      <button type="button" className="text-xs text-red-700 underline dark:text-red-300" onClick={() => void report(e.id)}>
                        Not me
                      </button>
                    ),
                },
              ]}
            />
          </div>
          <div className="mt-4">
            <DataTable
              caption="Changes to your account"
              rows={data.changes}
              rowKey={(c) => c.id}
              empty={<EmptyState title="No changes to your account in the last 90 days" />}
              columns={[
                { key: "when", header: "When", render: (c) => <span title={c.at}>{fmt.when(c.at)}</span> },
                { key: "what", header: "Change", render: (c) => <code className="text-xs">{c.action}</code> },
                { key: "by", header: "By", render: (c) => (c.viaGairm ? `${c.actor} (Gairm staff)` : c.actor) },
                { key: "result", header: "Result", render: (c) => c.result },
              ]}
            />
          </div>
        </>
      )}
    </Card>
  );
}
